import { ClientLibraryType, QueryFilter } from '@bett3r-dev/teselly-client-library';

/**
 * Fetch per-membership roles for a set of users.
 *
 * Queries the filtered `-search` route (`IdentityAuthUserSecuritySearch`) with a
 * `userId in [...]` filter. The base `IdentityAuthUserSecurity` route declares no
 * query schema and REJECTS `filter`, so it never returned the members' rows —
 * leaving `membershipRoles` empty for every user (TV1-1968). The `-search`
 * variant declares `schemas.query` and supports the filter.
 *
 * Each returned security row carries `memberships[accountId]` (the role NAMES
 * for that account). We map it into `{ [userId]: roleNames[] }`, keyed by
 * `row.userId` and read from `row.memberships?.[accountId]` — preserving the
 * original mapping semantics against the new row shape.
 *
 * @returns Record<userId, roleNames[]> for every user with a membership in
 *          `accountId`. Users without a membership entry are omitted.
 */
export const fetchMembershipRoles = async (
  backend: ClientLibraryType,
  accountId: string,
  userIds: string[]
): Promise<Record<string, string[]>> => {
  const rolesMap: Record<string, string[]> = {};
  if ( userIds.length === 0 ) return rolesMap;

  const filter: QueryFilter<any>[] = [{ field: 'userId', op: 'in', value: userIds }];
  const result = await backend.queries.IdentityAuthUserSecuritySearch({ filter });
  const rows = Array.isArray( result ) ? result : ( result?.items ?? []);

  for ( const row of rows ) {
    const orgRoles = row?.memberships?.[accountId];
    if ( Array.isArray( orgRoles )) rolesMap[row.userId] = orgRoles;
  }

  return rolesMap;
};
